/**
 * The one copy of the partner referral-link rules. apps/web (the `?via=` hop),
 * apps/edge (`/api/ref/<code>`), apps/api (custom links, click recording) and
 * apps/partner (the create-link drawer) all import from here; none keeps its own
 * pattern. No runtime dependencies, nothing beyond the standard `URL`.
 */

/** A partner referral code: 3–32 chars of a-z, 0-9 and inner hyphens. */
export const REFERRAL_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/;

/** A custom-link tag: 1–32 chars of a-z, 0-9 and inner hyphens. */
export const LINK_TAG_PATTERN =
  /^(?:[a-z0-9][a-z0-9-]{0,30}[a-z0-9]|[a-z0-9])$/;

export const isLinkTag = (value: unknown): value is string =>
  typeof value === "string" && LINK_TAG_PATTERN.test(value);

/** The query parameter that carries the tag: `<page>?via=<code>&l=<tag>`. */
export const LINK_TAG_PARAM = "l";

/**
 * Params that never survive into a landing path. `l` is read from the edge
 * request's own query (never from `to`) and is never passed on.
 */
export const RESERVED_REFERRAL_PARAMS: readonly string[] = [
  "via",
  "ref",
  "nt_ref",
  LINK_TAG_PARAM,
];

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;

function hasControlOrBackslash(value: string): boolean {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f || char === "\\") return true;
  }
  return false;
}

function decodeFully(value: string): string | null {
  let current = value;
  for (let round = 0; round < 3; round += 1) {
    let next: string;
    try {
      next = decodeURIComponent(current);
    } catch {
      return null;
    }
    if (next === current) return current;
    current = next;
  }
  return current;
}

/** `/r` and `/api` (and below): the referral hop and the Edge Worker's own routes. */
export function isBlockedPrefix(path: string): boolean {
  const lower = path.toLowerCase();
  return (
    lower === "/r" ||
    lower === "/api" ||
    lower.startsWith("/r/") ||
    lower.startsWith("/api/")
  );
}

/** A relative, same-origin landing path; anything doubtful becomes "/". */
export function sanitizeTo(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
  if (hasControlOrBackslash(raw)) return "/";
  const decoded = decodeFully(raw);
  if (
    decoded === null ||
    hasControlOrBackslash(decoded) ||
    decoded.startsWith("//") ||
    SCHEME.test(decoded) ||
    isBlockedPrefix(decoded)
  ) {
    return "/";
  }
  let parsed: URL;
  try {
    parsed = new URL(raw, "https://landing.invalid");
  } catch {
    return "/";
  }
  if (parsed.origin !== "https://landing.invalid") return "/";
  if (isBlockedPrefix(parsed.pathname) || parsed.pathname.startsWith("//")) {
    return "/";
  }
  for (const name of RESERVED_REFERRAL_PARAMS) parsed.searchParams.delete(name);
  return `${parsed.pathname}${parsed.search}`;
}

/** A path the web Worker's `?via=` hop handles: not `/internal/` or `/api/`, and not a file. */
export function isPagePath(pathname: string): boolean {
  if (pathname.startsWith("/internal/") || pathname.startsWith("/api/")) {
    return false;
  }
  return !pathname.slice(pathname.lastIndexOf("/") + 1).includes(".");
}

/**
 * A path the web Worker's `?via=` hop never sees, so a link to it records no
 * click: a file, `/internal/`, or a path another Worker's route serves on the
 * site host (edge `/m/*`, MCP `/mcp*` and `/.well-known/*`).
 */
export function isUntrackedPath(path: string): boolean {
  const lower = path.toLowerCase();
  if (path.slice(path.lastIndexOf("/") + 1).includes(".")) return true;
  return (
    lower.startsWith("/internal/") ||
    lower === "/m" ||
    lower.startsWith("/m/") ||
    lower.startsWith("/mcp") ||
    lower.startsWith("/.well-known/")
  );
}

export const DESTINATION_MAX = 255;
const SITE_HOSTS = new Set([
  "nonresident.tax",
  "www.nonresident.tax",
  "test.nonresident.tax",
]);

/**
 * A custom link's destination: a same-site path, or a full nonresident.tax URL
 * reduced to its path + query, sanitised like edge and put in the site's
 * trailing-slash form. Null when refused.
 */
export function normalizeDestination(input: unknown): string | null {
  if (typeof input !== "string") return null;
  const raw = input.trim();
  if (!raw || raw.length > DESTINATION_MAX) return null;
  let candidate = raw;
  if (/^https?:\/\//i.test(raw)) {
    let url: URL;
    try {
      url = new URL(raw);
    } catch {
      return null;
    }
    if (
      !SITE_HOSTS.has(url.hostname.toLowerCase()) ||
      url.port ||
      url.username ||
      url.password
    ) {
      return null;
    }
    candidate = `${url.pathname}${url.search}`;
  }
  if (!candidate.startsWith("/")) return null;
  const clean = sanitizeTo(candidate);
  const rawPath = candidate.split(/[?#]/)[0] ?? "";
  // sanitizeTo answers "/" for anything doubtful; only a real root path may come back as "/".
  if (clean === "/" && rawPath !== "/") return null;
  const queryAt = clean.indexOf("?");
  const path = queryAt === -1 ? clean : clean.slice(0, queryAt);
  const query = queryAt === -1 ? "" : clean.slice(queryAt);
  if (isUntrackedPath(path)) return null;
  const canonical = path.endsWith("/") ? path : `${path}/`;
  const out = `${canonical}${query}`;
  return out.length <= DESTINATION_MAX ? out : null;
}

/** Default tag from a link name: lowercase words joined by hyphens, at most 32 chars. */
export function slugifyTag(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32)
    .replace(/-+$/g, "");
  return slug || "link";
}

/** The custom link as shared: `<origin><destination>?via=<code>&l=<tag>`. */
export function customLinkUrl(
  origin: string,
  destination: string,
  code: string,
  tag: string,
): string {
  const sep = destination.includes("?") ? "&" : "?";
  return `${origin}${destination}${sep}via=${code}&${LINK_TAG_PARAM}=${tag}`;
}
